import { useFormContext } from '@/providers'
import { isNumber, isValidDate, useModal } from '@/utils'
import { useCallback, useEffect, useState } from 'react'
import { MonthSelect } from '../MonthSelect'
import { FormItem } from './Component'
import { ModalWrapper } from './ModalWrapper'
import { DropdownModalTrigger, HelperMessage } from './styles'
import { ThemeColor } from '@/enums'
import { Column } from '@/components/styled'

interface MonthSelectValue {
  [key: string]: number | undefined
}

export interface FormMonthSelectValue {
  month?: number
  year?: number
}

type FormValue = number | FormMonthSelectValue | undefined

interface FormMonthSelectProps {
  name: string
  label?: string
  initialValue?: number | FormMonthSelectValue
  info?: string
  helperText?: string
  ignoreErrorKeys?: string[]
  showErrorHelperText?: boolean
  shouldRenderInModal?: boolean
  [key: string]: any
}

interface MonthSelectModalWrapperProps {
  children: React.ReactNode
  open: () => void
  close: () => void
  isOpen: boolean
  currentField: string | null
}

interface ComponentProps {
  onChange?: (value: FormValue, shouldValidate?: boolean) => void
  value?: FormValue
  errorText?: string
  [key: string]: any
}

const makeValue = (value: FormValue): MonthSelectValue => {
  const date = new Date(value as number)
  if (isNumber(value) && isValidDate(date)) {
    return {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    }
  }

  return value as MonthSelectValue
}

const MonthSelectModalWrapper: React.FC<MonthSelectModalWrapperProps> = ({
  children,
  open,
  close,
  isOpen,
}) => (
  <>
    <DropdownModalTrigger onClick={open}>
      {!isOpen && children}
    </DropdownModalTrigger>
    <ModalWrapper close={close} isOpen={isOpen}>
      <Column gap="4px">{children}</Column>
    </ModalWrapper>
  </>
)

export const FormMonthSelect: React.FC<FormMonthSelectProps> = ({
  name,
  label,
  initialValue,
  info,
  helperText,
  ignoreErrorKeys,
  showErrorHelperText,
  shouldRenderInModal,
  ...props
}) => {
  const { setErrors } = useFormContext()

  const { open, close, isOpen } = useModal()
  const [currentField, setCurrentField] = useState<string | null>(null)

  useEffect(() => {
    if (currentField && isOpen) {
      const element = document.querySelector(currentField) as HTMLElement
      element?.focus()
      setCurrentField(null)
    }
  }, [currentField])

  const Component = useCallback(
    ({ onChange, value, errorText, ...field }: ComponentProps) => {
      const handleOnChange = ({ month, year }: FormMonthSelectValue) => {
        setErrors({})
        if (month && year) {
          onChange?.(Date.parse(new Date(year, month - 1).toISOString()), false)
          if (shouldRenderInModal) close()
        } else {
          onChange?.({ month, year }, false)
        }
      }

      return shouldRenderInModal ? (
        <MonthSelectModalWrapper
          close={close}
          currentField={currentField}
          isOpen={isOpen}
          open={open}
        >
          <MonthSelect
            onChange={handleOnChange}
            value={makeValue(value)}
            {...field}
            {...props}
            monthKey="month"
            setCurrentField={setCurrentField}
            yearKey="year"
          />
          {/* Fixme: bit of a hack, needs refactoring the useCallback with some time */}
          {errorText && isOpen && (
            <HelperMessage asChild color={ThemeColor.danger2}>
              {errorText}
            </HelperMessage>
          )}
        </MonthSelectModalWrapper>
      ) : (
        <MonthSelect
          onChange={handleOnChange}
          value={makeValue(value)}
          {...field}
          {...props}
          monthKey="month"
          yearKey="year"
        />
      )
    },
    [isOpen, shouldRenderInModal],
  )

  return (
    <FormItem
      helperText={helperText}
      ignoreErrorKeys={ignoreErrorKeys}
      info={info}
      initialValue={initialValue}
      label={label}
      name={name}
      noMargin
      showErrorHelperText={showErrorHelperText}
    >
      <Component />
    </FormItem>
  )
}
